import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProposal, updateProposal, sendProposal } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { StatusDot } from '@/components/ui/status-dot';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

export default function ProposalEdit() {
  const { id } = useParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getProposal(id).then(r => { setProposal(r.data); setLoading(false); }).catch(() => navigate('/proposals'));
  }, [id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProposal(id, { title: proposal.title, content: proposal.content, total_amount: proposal.total_amount });
      toast.success('Proposal saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await handleSave();
      const res = await sendProposal(id);
      setProposal(prev => ({ ...prev, ...res.data }));
      toast.success('Proposal sent!');
    } catch { toast.error('Failed to send'); }
    setSending(false);
  };

  const copyLink = () => {
    const link = `${window.location.origin}/p/${proposal.token}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  if (loading) return <div className="h-64 animate-pulse rounded-md bg-wash" />;
  if (!proposal) return null;

  return (
    <div className="mx-auto max-w-[760px]" data-testid="proposal-edit-page">
      <button
        onClick={() => navigate('/proposals')}
        className="mb-4 inline-flex items-center gap-1.5 text-small text-ink-muted transition-colors hover:text-forest"
      >
        <ArrowLeft strokeWidth={1.5} className="h-3.5 w-3.5" /> Proposals
      </button>

      <PageHeader
        title={proposal.title}
        description={proposal.client?.name}
        actions={
          <>
            {proposal.status === 'sent' && (
              <>
                <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-link-btn">Copy link</Button>
                <Button variant="outline" size="sm" asChild>
                  <a href={`/p/${proposal.token}`} target="_blank" rel="noreferrer">View public page</a>
                </Button>
              </>
            )}
            {proposal.status === 'draft' && (
              <Button size="sm" onClick={handleSend} disabled={sending} data-testid="send-proposal-btn">
                {sending ? 'Sending…' : 'Send to client'}
              </Button>
            )}
          </>
        }
      />

      <div className="flex items-center gap-4">
        <StatusDot status={proposal.status} />
        {saving && <span className="text-small text-ink-subtle">Saving…</span>}
      </div>

      {proposal.status === 'rejected' && proposal.rejection_reason && (
        <div className="mt-6 border-l-2 border-destructive/40 pl-4">
          <p className="text-micro uppercase text-ink-subtle">Rejection reason</p>
          <p className="mt-1 whitespace-pre-wrap text-body text-ink">{proposal.rejection_reason}</p>
        </div>
      )}

      <ProposalPreview proposal={proposal} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-micro uppercase text-ink-subtle">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function ProposalPreview({ proposal }) {
  const content = proposal.content || {};
  return (
    <article className="mt-10 border-t border-line pt-10" data-testid="proposal-preview">
      <div className="border-b border-line pb-8">
        <h1 className="font-display text-display-l text-ink">{proposal.title}</h1>
        {proposal.client && (
          <p className="mt-4 text-body text-ink-muted">
            Prepared for {proposal.client.name}{proposal.client.company ? ` at ${proposal.client.company}` : ''}
          </p>
        )}
      </div>

      <div className="max-w-[680px] space-y-10 pt-10">
        {content.summary && (
          <Section title="Executive summary">
            <p className="text-body text-ink-muted">{content.summary}</p>
          </Section>
        )}

        {content.client_needs?.length > 0 && (
          <Section title="Understanding your needs">
            <ul className="space-y-2">
              {content.client_needs.map((n, i) => (
                <li key={i} className="flex items-start gap-2.5 text-body text-ink-muted">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-ink-subtle" />
                  {n}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {content.solution && (
          <Section title="Proposed solution">
            <p className="whitespace-pre-wrap text-body text-ink-muted">{content.solution}</p>
          </Section>
        )}

        {content.timeline?.length > 0 && (
          <Section title="Project timeline">
            <div>
              {content.timeline.map((t, i) => (
                <div key={i} className="flex gap-5 border-t border-line py-4">
                  <span className="text-micro uppercase tabular-nums text-ink-subtle">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <p className="text-body text-ink">
                      {t.milestone} <span className="text-ink-subtle">({t.duration})</span>
                    </p>
                    <p className="mt-1 text-small text-ink-muted">{t.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {content.pricing?.length > 0 && (
          <Section title="Investment">
            <div>
              {content.pricing.map((p, i) => (
                <div key={i} className="flex items-start justify-between gap-6 border-t border-line py-4">
                  <div>
                    <p className="text-body text-ink">{p.item}</p>
                    {p.description && <p className="mt-0.5 text-small text-ink-muted">{p.description}</p>}
                  </div>
                  <span className="shrink-0 text-body tabular-nums text-ink">{formatCurrency(p.amount)}</span>
                </div>
              ))}
              <div className="flex items-baseline justify-between border-t border-line-strong pt-4">
                <span className="text-micro uppercase text-ink-subtle">Total investment</span>
                <span className="font-sans text-metric tabular-nums text-ink">{formatCurrency(proposal.total_amount)}</span>
              </div>
            </div>
          </Section>
        )}

        {content.terms && (
          <Section title="Terms & conditions">
            <p className="whitespace-pre-wrap text-small text-ink-muted">{content.terms}</p>
          </Section>
        )}
      </div>
    </article>
  );
}
